import {
  authSession,
  ensureFreshTokenIfAuthenticated,
} from "@/authentication/authenticationService"
import { MyAccountRoute } from "@/constants/routes/myAccount.ts"
import { isAbsoluteUrl } from "@/lib/isAbsoluteUrl.ts"
import nativeHelper from "@/lib/nativeHelper.ts"
import { removeLeadingSlash, removeTrailingSlash } from "@/lib/utils.ts"
import TrackingService from "@/services/TrackingService"
import { unref } from "vue"
import { NavigationGuard } from "vue-router"

export const authGuard: NavigationGuard = async (to) => {
  const requiresAnon = to.matched.some((record) => record.meta.requiresAnon)
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)

  if (requiresAnon && authSession.hasValidRefreshTokenCookie()) {
    return "/"
  }

  /**
   * If we are currently inside an in-app browser, hitting the refresh endpoint
   * can cause conflicts with the app's session cookie so we don't want to do
   * it in that case, plus we can assume we're about to be closed down anyway,
   * since the app will want to handle whatever route we're currently on.
   **/
  if (requiresAuth && !nativeHelper.isInAppBrowser()) {
    await ensureFreshTokenIfAuthenticated()
    const isAuthenticated = unref(authSession.isAuthenticated)

    if (!isAuthenticated) {
      let queryWithRedirect = ""
      const toFullPath = removeLeadingSlash(to.fullPath)
      const toPath = removeLeadingSlash(to.path)
      const isHomePath = removeTrailingSlash(to.path) === MyAccountRoute.home
      if (!isHomePath || to.query.redirect) {
        if (toPath === to.query.redirect) {
          queryWithRedirect = `?redirect=${encodeURIComponent(toPath)}`
        } else {
          queryWithRedirect = `?redirect=${encodeURIComponent(toFullPath)}`
        }
      }

      TrackingService.sendEvent("redirectToLogin")
      return `${MyAccountRoute.login}${queryWithRedirect}`
    }
    if (isAuthenticated && to.query.redirect) {
      TrackingService.sendEvent("loginRedirectSuccessful")
      const toRedirectUrl = decodeURIComponent(to.query.redirect as string)
      if (isAbsoluteUrl(toRedirectUrl)) {
        window.location.assign(toRedirectUrl)
        return
      }

      const { redirect: _redirect, ...rest } = to.query
      // toRedirectUrl is a same-origin relative path; parse it so its own query + hash survive
      const target = new URL(toRedirectUrl, window.location.origin)
      return {
        path: target.pathname,
        query: { ...Object.fromEntries(target.searchParams), ...rest },
        hash: target.hash,
      }
    }
  }
}
